import { useState, useEffect, useRef } from 'react'

/**
 * Types out a string character by character. Calls `onComplete` once
 * the full string is rendered. Designed to be remounted (via parent
 * `key`) when the text changes — that's how the demo state machines
 * reset between cycles.
 *
 * Props:
 *   - text:         the string to type out
 *   - speed:        characters per second (default 35)
 *   - initialDelay: ms before typing starts (default 0)
 *   - onComplete:   callback once the full text is shown
 *   - showCursor:   if true, renders a soft pulse where the cursor is
 *   - className:    passed through to the inner span
 */
export default function TypingText({
  text,
  speed = 35,
  initialDelay = 0,
  onComplete,
  showCursor = false,
  className,
}) {
  const [displayed, setDisplayed] = useState('')
  const onCompleteRef = useRef(onComplete)

  // Keep the callback ref fresh without retriggering the typing effect.
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    setDisplayed('')
    if (!text) {
      onCompleteRef.current?.()
      return
    }

    let index = 0
    let interval
    let timeout

    const tick = () => {
      interval = setInterval(() => {
        index += 1
        setDisplayed(text.slice(0, index))
        if (index >= text.length) {
          clearInterval(interval)
          // small pause so the eye registers completion before the
          // parent moves to the next phase
          timeout = setTimeout(() => onCompleteRef.current?.(), 250)
        }
      }, 1000 / speed)
    }

    if (initialDelay > 0) {
      timeout = setTimeout(tick, initialDelay)
    } else {
      tick()
    }

    return () => {
      if (interval) clearInterval(interval)
      if (timeout) clearTimeout(timeout)
    }
  }, [text, speed, initialDelay])

  return (
    <span className={className}>
      {displayed}
      {showCursor && displayed.length < text.length && (
        <span
          aria-hidden="true"
          className="inline-block w-[1px] ml-0.5 animate-pulse"
          style={{ backgroundColor: 'currentColor', height: '1em', verticalAlign: 'text-bottom' }}
        />
      )}
    </span>
  )
}
